import $ from 'jquery';
import debounce from 'just-debounce';
import CurrencyConverter from 'shopify-currency-converter';

import Search from '../../Search';
import layout from '../../Layout';
import SearchForm from './SearchForm';

import {
  ContentResults,
  ContentNoResults,
  ProductResults,
  ProductNoResults,
  ResultsFooter,
} from './LiveSearchTemplates';

const MAX_RESULTS = 7;

export default class LiveSearch {
  constructor($els, settings) {
    this.$el = $els.$el;
    this.$header = $els.$header;

    this.closeEvents = [];

    this.settings = settings;
    this.enableContent = this.settings.enable_content;
    this.enableImages = this.settings.enable_images;
    this.searchType = this.enableContent ? 'article,page,product' : 'product';
    this.category = '';

    const $form = this.$el.find('[data-live-search-form]');

    this.$input = $form.find('[data-live-search-input]');
    this.$button = $form.find('[data-live-search-submit]');
    this.$takeOverButton = $form.find('[data-live-search-takeover-cancel]');
    this.$filter = this.$el.find('[data-live-search-filter]');

    this.$flyDown = this.$el.find('[data-live-search-flydown]');
    this.$searchResults = this.$flyDown.find('[data-live-search-results]');
    this.$searchPlaceholder = this.$flyDown.find('[data-live-search-placeholder]');
    this.$quickLinks = this.$flyDown.find('[data-live-search-quick-links]');

    this.staticSearch = new SearchForm(
      this.$el,
      {
        liveSearch: true,
        setCategory: category => { this.category = category; },
      },
    );

    this.search = new Search({
      view: 'header',
      type: this.searchType,
    });

    this._search = this._search.bind(this);
    this._documentFocus = this._documentFocus.bind(this);

    this._searchCancelled = this._searchCancelled.bind(this);
    this._searchComplete = this._searchComplete.bind(this);
    this._searchError = this._searchError.bind(this);

    this.search.on('cancelled', this._searchCancelled);
    this.search.on('complete', this._searchComplete);
    this.search.on('error', this._searchError);

    this._closeEsc = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        this.$input.focus();
        this._closeFlyDown(true);
        this._closeTakeOver();
      }
    };

    this.events = [
      this.$input.on('keyup.live-search', debounce(this._search, 250)),
      this.$input.on('focus.live-search', event => {
        event.stopPropagation();
        this._onSearchFocus();
      }),
      this.$takeOverButton.on('click.live-search', event => {
        event.preventDefault();
        this._closeTakeOver();
      }),
      this.$filter.on('change.live-search', this._search),
    ];
  }

  open() {
    // If is XS, pop the search out into a Takeover screen
    if (layout.isLessThanBreakpoint('S')) {
      this._openTakeOver();
    }

    if (this._shouldOpenFlyDown()) {
      this._openFlyDown();
    }

    this.$input.focus();
  }

  unload() {
    this.events.forEach($el => $el.off('.live-search'));

    this.search.off('cancelled', this._searchCancelled);
    this.search.off('complete', this._searchComplete);
    this.search.off('error', this._searchError);

    this.staticSearch.unload();
  }

  _onSearchFocus() {
    this.$el.addClass('live-search--focused');

    // If is XS, pop the search out into a Takeover screen
    if (layout.isLessThanBreakpoint('S')) {
      this._openTakeOver();
    }

    if (this._shouldOpenFlyDown()) {
      this._openFlyDown();
    }
  }

  _search(event) {
    // Ignore non character key strokes
    const invalidKeyStrokes = [
      'Alt',
      'ArrowRight',
      'ArrowLeft',
      'ArrowUp',
      'ArrowDown',
      'Capslock',
      'Control',
      'Escape',
      'Meta',
      'Shift',
      'Tab',
      'Enter',
    ];

    if (event.key && invalidKeyStrokes.indexOf(event.key) !== -1) {
      return;
    }

    let terms = this.$input.val();
    const hasTerms = terms.length > 0;

    // If is XS, pop the search out into a Takeover screen
    if (layout.isLessThanBreakpoint('S')) {
      this._openTakeOver();
    }

    // Clear previous results
    this.$searchResults.html('');

    // If terms has length, this will be true
    this._toggleButton(hasTerms);

    if (hasTerms) {
      this._openFlyDown();
      this._updateFlyDown(true);

      // Adds `*` after each word in search terms, doesn't add to last word
      if (terms.indexOf(' ') > 0) {
        terms = terms.split(' ').join('* ').trim();
      }

      // Adds `*` at the end of the search terms
      terms = `${terms}*`;

      const filter = this.$filter[0];

      // Add field filter
      terms = filter && filter.value ? `${terms} ${filter.value}` : terms;

      this.search.execute(terms);
    } else {
      this._closeFlyDown(true);
    }
  }

  _searchError(response) {
    console.warn('Search had error');
    console.log(response.message, response.error, response.event);
    this._toggleButton(false);
  }

  /**
   * Search has been cancelled due to new search being performed
   *
   * @private
   */
  _searchCancelled() {
    // Searches are canceled on user interaction, UI should remain unchanged
  }

  _searchComplete(response) {
    let {
      content,
      products,
    } = response;

    const {
      terms,
      sanitizedTerms,
    } = response;

    content = content.slice(0, MAX_RESULTS);
    products = products.slice(0, MAX_RESULTS);

    const {
      enable_images: enableImages,
      context,
    } = this.settings;
    const settings = {
      products: {
        results: {
          products,
          enableImages,
        },
        noResults: {
          context,
          terms: sanitizedTerms,
          category: this.category,
        },
      },
      content: {
        results: {
          context,
          content,
        },
        noResults: {
          context,
        },
      },
      footer: {
        context,
        terms,
        type: this.searchType,
      },
    };

    const $productResults = products.length
      ? ProductResults(settings.products.results)
      : ProductNoResults(settings.products.noResults);

    const $contentResults = content.length
      ? ContentResults(settings.content.results)
      : ContentNoResults(settings.content.noResults);

    $productResults.find('.money').each((i, el) => {
      el.innerHTML = Shopify.formatMoney( // eslint-disable-line no-param-reassign
        el.innerHTML,
        this.settings.money_format,
      );
      CurrencyConverter.update(el);
    });

    // Hide placeholder
    // TODO: Lock minimum height?
    this._updateFlyDown();

    // Show results
    this.$searchResults
      .html($productResults)
      .addClass('visible');

    if (this.enableContent) {
      this.$searchResults
        .append($contentResults);
    }

    this.$searchResults
      .append(ResultsFooter(settings.footer));

    this._toggleButton(false);
  }

  /**
   * Toggles search button while processing
   *
   * @param disable
   * @private
   */
  _toggleButton(disable) {
    if (disable) {
      this.$button
        .addClass('search-icon--processing')
        .attr('disabled');
    } else {
      this.$button
        .removeClass('search-icon--processing')
        .removeAttr('disabled');
    }
  }

  _shouldOpenFlyDown() {
    const hasTerms = this.$input.val().length > 0;
    const hasResults = this.$searchResults.children().length > 0;

    if (hasTerms && hasResults) {
      return true;
    }

    if (this.$quickLinks.length) {
      return true;
    }

    return false;
  }

  _openFlyDown() {
    if (this.$flyDown.data('is-open')) {
      return;
    }

    const $window = $(window);
    const $document = $(document);

    // Only listen for events outside of the search flydown when its open
    this.closeEvents = [
      $window.on('keydown.live-search', this._closeEsc),
      $document.on('focusin.live-search', this._documentFocus),
      $document.on('touchstart.live-search', this._documentFocus),
      $document.on('click.live-search', this._documentFocus),
    ];

    this._updateFlyDown();

    this.$el.addClass('live-search--active');
    this.$flyDown.data('is-open', true);
  }

  _updateFlyDown(placeholder = false) {
    const hasTerms = this.$input.val().length > 0;
    const hasNoResults = this.$searchResults.find('[data-live-search-no-products]').length === 0;
    const hasPreviousResults = this.$searchResults.children().length > 0;
    const hasQuickLinks = this.$quickLinks.length;

    if (placeholder) {
      this.$quickLinks.removeClass('visible');
      this.$searchResults.removeClass('visible');
      this.$searchPlaceholder.addClass('visible');
    } else {
      this.$searchPlaceholder.removeClass('visible');

      if (hasTerms && hasNoResults && hasPreviousResults) {
        this.$searchResults.addClass('visible');
        this.$quickLinks.removeClass('visible');
      } else if (hasQuickLinks) {
        this.$searchResults.removeClass('visible');
        this.$quickLinks.addClass('visible');
      }
    }
  }

  /**
   * Close the FlyDown when no longer needed
   *  - Keep focus styling if input is still being interacted with
   *
   * @param retainFocus
   * @private
   */
  _closeFlyDown(retainFocus = false) {
    if (!this.$flyDown.data('is-open')) {
      return;
    }

    this.$flyDown.data('is-open', false);
    this.$el.removeClass('live-search--active');

    if (!retainFocus) {
      this.$el.removeClass('live-search--focused');
    }

    this.closeEvents.forEach($el => $el.off('.live-search'));
    this.closeEvents = [];
  }

  _openTakeOver() {
    if (this.$header.hasClass('search--section')) {
      document.body.classList.add('search-takeover-active');
    }
    document.body.classList.add('scroll-lock');
    this.$el.addClass('live-search--takeover');
  }

  _closeTakeOver() {
    document.body.classList.remove('scroll-lock');
    document.body.classList.remove('search-takeover-active');
    this.$el.removeClass('live-search--takeover');
  }

  /**
   * When the focus element has changed, either by clicking, touching, or tabbing
   * check to see if it is within the flydown
   *
   * @param event
   * @private
   */
  _documentFocus(event) {
    const $closest = $(event.target).closest('[data-live-search]');

    if ($closest.length) {
      return;
    }

    this._closeFlyDown();
    this._closeTakeOver();
  }
}
